import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { Box, Divider } from "@mui/material";
import { getElementToRender } from "./RenderElements/ElementDefinitions";
import CollapsableSection from "./RenderElements/CollapsableSection";
import { Info } from "@mui/icons-material";

const FormRenderer = forwardRef((props, ref) => {
  const { formTemplate, forceReadOnly } = props;
  const [formValues, setFormValues] = useState(
    (formTemplate || []).map((t) => ({ id: t.id, value: t.value }))
  );
  const [formScores, setFormScores] = useState([]);

  useImperativeHandle(ref, () => ({
    isFormValid() {
      const mergedFormTemplateAndValues = formTemplate.map((t) => ({
        required: t.required,
        label: t.label,
        value: formValues.find((u) => u.id === t.id)?.value,
      }));

      const formElementsMarkedAsRequiredAndMissingValue =
        mergedFormTemplateAndValues.filter(
          (t) => t.required && (t.value === undefined || t.value === "")
        );

      return {
        isValid: formElementsMarkedAsRequiredAndMissingValue.length === 0,
        errors: formElementsMarkedAsRequiredAndMissingValue
          .map((t, i) => `${i + 1}. ${t.label}`)
          .join("\n"),
      };
    },
    getFormData() {
      return formValues;
    },
  }));

  const getRecursivelyFormulaIds = (formData) => {
    const formulaIds = [];
    formData.map((el) => {
      if (el.formElement === "Formula") {
        formulaIds.push({ formulaId: el.id, formData });
      }

      if (
        el.enableDependentElements &&
        el.dependentElements &&
        el.dependentElements.length > 0
      ) {
        const _formulaIds = getRecursivelyFormulaIds(el.dependentElements);
        formulaIds.push(..._formulaIds);
      }
    });

    return formulaIds;
  };

  const getRecursivelyFormulaScores = (formulaId, formData) => {
    const scores = [];
    formData.map((el) => {
      if (el.formElement === "Section") {
        if (
          el.enableDependentElements &&
          el.dependentElements &&
          el.dependentElements.length > 0
        ) {
          const _scores = getRecursivelyFormulaScores(
            formulaId,
            el.dependentElements
          );
          scores.push(..._scores);
        }
      }

      if (
        !el ||
        !(
          el.formElement === "YesNoInput" ||
          el.formElementDisplayName === "Number Input" ||
          el.formElement === "Formula"
        ) ||
        !el.score ||
        (el.formElement !== "Formula" && el.value === undefined)
      ) {
        return [];
      }

      const elementScore = el.score?.find(
        (s) => s.formulaId === formulaId
      )?.score;
      if (!elementScore) {
        return [];
      }

      let actualScore = 0;
      let maxScore = 0;
      let useFormulaScoringForCalculation;
      if (el.formElement === "YesNoInput") {
        const yesScore = parseFloat(elementScore.YesScore);
        const noScore = parseFloat(elementScore.NoScore);

        actualScore = el.value ? elementScore.YesScore : elementScore.NoScore;
        maxScore = Math.max(yesScore, noScore);
      } else if (el.formElementDisplayName === "Number Input") {
        if (elementScore.NumberInputBasedOnEnteredValue) {
          actualScore = parseFloat(el.value);
          maxScore = parseFloat(el.value);
        } else {
          actualScore = parseFloat(elementScore.NumberInput);
          maxScore = parseFloat(elementScore.NumberInput);
        }
      } else if (el.formElement === "Formula") {
        if (elementScore.UseFormulaScoringForCalculation !== undefined) {
          useFormulaScoringForCalculation =
            elementScore.UseFormulaScoringForCalculation;
        }
      }

      let scoreToPush = {
        id: el.id,
        score: { actualScore: actualScore, maxScore: maxScore },
      };
      if (useFormulaScoringForCalculation !== undefined) {
        scoreToPush = {
          ...scoreToPush,
          useFormulaScoringForCalculation: useFormulaScoringForCalculation,
        };
      }
      scores.push(scoreToPush);

      if (
        el.enableDependentElements &&
        el.dependentElements &&
        el.dependentElements.length > 0
      ) {
        const _scores = getRecursivelyFormulaScores(
          formulaId,
          el.dependentElements
        );
        scores.push(..._scores);
      }
    });

    return scores;
  };

  const loadSavedScores = () => {
    // This is the new scheme. It uses formulaId to calculate the scores.
    const formulaIds = getRecursivelyFormulaIds(formTemplate);
    const scores = formulaIds.map((f) => {
      return {
        id: f.formulaId,
        scores: getRecursivelyFormulaScores(f.formulaId, f.formData),
      };
    });
    return scores;
  };

  const getChildElementIdsThatHaveScoringEnabled = (formElement) => {
    const response = formElement.dependentElements?.map((d) => {
      if (d.formElement !== "YesNoInput" || d.formElement === "Section") {
        return [];
      }

      if (d.enableDependentElements && d.dependentElements?.length > 0) {
        return [d.id, ...getChildElementIdsThatHaveScoringEnabled(d).flat()];
      } else {
        return d.id;
      }
    });

    return response?.flat() ?? [];
  };

  const onChange = (id, value, extension, scores, formElement) => {
    //Extension value will be just on files input
    let newFormValue = {
      id: id,
      value: value,
    };

    if (extension) {
      newFormValue = {
        ...newFormValue,
        extension,
      };
    }

    // Scores state management.
    if (scores && scores.length > 0) {
      const elementsToFilter = [
        formElement.id,
        ...getChildElementIdsThatHaveScoringEnabled(formElement).flat(),
      ];
      const newFormScores = formScores.map((formulaScores) => {
        const filteredScores = formulaScores.scores.filter((s) => {
          return !elementsToFilter.includes(s.id);
        });

        let newScores = [];
        if (value === undefined) {
          newScores = filteredScores;
        } else {
          const _score = scores.find((s) => s.formulaId === formulaScores.id);
          if (_score) {
            newScores = [
              ...filteredScores,
              {
                id,
                score: {
                  actualScore: _score.actualScore,
                  maxScore: _score.maxScore,
                },
              },
            ];
          } else {
            newScores = filteredScores;
          }
        }

        return { id: formulaScores.id, scores: newScores };
      });

      setFormScores(newFormScores);
    }

    setFormValues((prevState) => [
      ...prevState.filter((t) => t.id !== id),
      newFormValue,
    ]);
  };

  useEffect(() => {
    setFormScores(loadSavedScores());
  }, []);

  const renderFormElement = (formElement, backgroundColor = undefined) => {
    let initialValue = formValues.find((u) => u.id === formElement.id);

    /*if (initialValue) {
      initialValue = initialValue;
    }*/

    const settings = formElement;
    if (forceReadOnly) {
      settings.readOnly = true;
    }

    return (
      <Box
        sx={{
          backgroundColor: backgroundColor,
          //marginBottom: 2,
        }}
        key={formElement.id}
      >
        {getElementToRender(
          settings,
          initialValue,
          onChange,
          formValues,
          formScores
        )}

        {settings.enableDependentElements &&
          (settings.dependentElements?.length ?? 0) > 0 &&
          settings.displayDependentElementsWhenValueIs ===
            formValues.find((u) => u.id === settings.id)?.value &&
          settings.dependentElements?.map((d) => renderFormElement(d))}
      </Box>
    );
  };

  return (
    <Box sx={{ flexDirection: "row", padding: 1 }}>
      <Box sx={{ flex: 1 }}>
        {formTemplate.map((el) => {
          let initialValue = formValues.filter((u) => u.id === el.id);

          if (initialValue[0]) {
            initialValue = initialValue[0];
          }

          const settings = el;
          if (forceReadOnly) {
            settings.readOnly = true;
          }

          return (
            <Box key={el.id}>
              <InformationSection
                informationSection={el.informationSection}
                renderFormElement={renderFormElement}
              />
              {el.formElement !== "Section" && renderFormElement(el)}

              {el.formElement === "Section" && (
                <CollapsableSection {...el} onChange={onChange}>
                  {formValues.find((u) => u.id === el.id)?.value === true &&
                    el.dependentElements?.map((d, index) => {
                      if (el.id !== null) {
                        return (
                          <>
                            {renderFormElement(d)}
                            {index !==
                              (el.dependentElements?.length ?? 0) - 1 && (
                              <Divider />
                            )}
                          </>
                        );
                      }
                    })}
                </CollapsableSection>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

FormRenderer.propTypes = {
  formTemplate: PropTypes.array.isRequired,
  formData: PropTypes.array,
  // overrides the form data settings and forces readonly on all form elements
  forceReadOnly: PropTypes.bool,
};

FormRenderer.defaultProps = {
  formData: [],
  forceReadOnly: false,
};

export default FormRenderer;

const InformationSection = ({ informationSection, renderFormElement }) => {
  const [showInformationSection, setShowInformationSection] = useState(
    informationSection?.showOnDefault
  );

  if (
    !informationSection ||
    (!informationSection.instructions &&
      (!informationSection.attachments ||
        informationSection.attachments.length === 0))
  ) {
    return <></>;
  }
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box
          sx={{
            borderRadius: "50%",
            height: 25,
            width: 25,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setShowInformationSection(!showInformationSection)}
        >
          <Info />
        </Box>
      </Box>
      {showInformationSection &&
        informationSection.instructions &&
        renderFormElement({
          ...informationSection.instructions,
          readOnly: true,
        })}
      {showInformationSection &&
        informationSection.attachments &&
        informationSection.attachments.length > 0 &&
        renderFormElement({
          ...informationSection.attachments[0],
          readOnly: true,
        })}
    </Box>
  );
};
